import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useState } from "react";
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import type { RecipeStand } from "../models/Recipe";

interface RecipeStandFormModalProps {
	open: boolean;
	initialData: RecipeStand | null;
	onSave: (stand: RecipeStand) => void;
	onCancel: () => void;
	isSaving: boolean;
}

export const RecipeStandFormModal = ({
	open,
	initialData,
	onSave,
	onCancel,
	isSaving,
}: RecipeStandFormModalProps) => {
	const [formData, setFormData] = useState<RecipeStand | null>(null);
	const [errors, setErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		if (open && initialData) {
			setFormData(sanitizeNullStrings({ ...initialData }));
			setErrors({});
		}
	}, [open, initialData]);

	if (!formData) return null;

	const handleChange = (field: keyof RecipeStand, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
		setErrors((prev) => ({ ...prev, [field]: "" }));
	};

	const validateRFactor = (value: number | null): boolean => {
		if (value == null) return true;
		return value >= 0.9 && value <= 2.0;
	};

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!validateRFactor(formData.RFactorTarget)) {
			newErrors.RFactorTarget = "Must be between 0.9 and 2.0";
		}
		if (!validateRFactor(formData.RFactorMin)) {
			newErrors.RFactorMin = "Must be between 0.9 and 2.0";
		}
		if (!validateRFactor(formData.RFactorMax)) {
			newErrors.RFactorMax = "Must be between 0.9 and 2.0";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = () => {
		if (!validate()) return;
		onSave(formData);
	};

	return (
		<Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ fontWeight: 600 }}>
				EDIT STAND: {formData.StandName}
			</DialogTitle>
			<DialogContent>
				<Grid2 container spacing={1.5} sx={{ mt: 0.5 }}>
					<Grid2 size={{ xs: 12 }}>
						<Typography
							sx={{
								color: "primary.main",
								fontWeight: 600,
								fontSize: "0.85rem",
							}}
						>
							Stand Configuration
						</Typography>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<TextField
							label="Roll Groove"
							type="number"
							value={formData.RollGroove ?? ""}
							onChange={(e) =>
								handleChange(
									"RollGroove",
									e.target.value === ""
										? null
										: Number.parseFloat(e.target.value),
								)
							}
							fullWidth
							size="small"
							slotProps={{
								inputLabel: { shrink: true },
								htmlInput: { step: 0.01 },
							}}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<TextField
							label="Looper Height"
							type="number"
							value={formData.LooperHeight ?? ""}
							onChange={(e) =>
								handleChange(
									"LooperHeight",
									e.target.value === ""
										? null
										: Number.parseFloat(e.target.value),
								)
							}
							fullWidth
							size="small"
							slotProps={{
								inputLabel: { shrink: true },
								htmlInput: { step: 0.1 },
							}}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<FormControlLabel
							control={
								<Checkbox
									checked={!!formData.IsTensionControlEnabled}
									onChange={(e) =>
										handleChange("IsTensionControlEnabled", e.target.checked)
									}
									size="small"
								/>
							}
							label="Tension Control"
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<FormControlLabel
							control={
								<Checkbox
									checked={!!formData.IsLooperControlEnabled}
									onChange={(e) =>
										handleChange("IsLooperControlEnabled", e.target.checked)
									}
									size="small"
								/>
							}
							label="Looper Control"
						/>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<Typography
							sx={{
								color: "primary.main",
								fontWeight: 600,
								fontSize: "0.85rem",
								mt: 1,
							}}
						>
							R-Factor (0.9 - 2.0)
						</Typography>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<TextField
							label="Target"
							type="number"
							value={formData.RFactorTarget ?? ""}
							onChange={(e) =>
								handleChange(
									"RFactorTarget",
									e.target.value === ""
										? null
										: Number.parseFloat(e.target.value),
								)
							}
							fullWidth
							size="small"
							error={!!errors.RFactorTarget}
							helperText={errors.RFactorTarget}
							slotProps={{
								inputLabel: { shrink: true },
								htmlInput: { step: 0.001, min: 0.9, max: 2.0 },
							}}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<TextField
							label="Min"
							type="number"
							value={formData.RFactorMin ?? ""}
							onChange={(e) =>
								handleChange(
									"RFactorMin",
									e.target.value === ""
										? null
										: Number.parseFloat(e.target.value),
								)
							}
							fullWidth
							size="small"
							error={!!errors.RFactorMin}
							helperText={errors.RFactorMin}
							slotProps={{
								inputLabel: { shrink: true },
								htmlInput: { step: 0.001, min: 0.9, max: 2.0 },
							}}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<TextField
							label="Max"
							type="number"
							value={formData.RFactorMax ?? ""}
							onChange={(e) =>
								handleChange(
									"RFactorMax",
									e.target.value === ""
										? null
										: Number.parseFloat(e.target.value),
								)
							}
							fullWidth
							size="small"
							error={!!errors.RFactorMax}
							helperText={errors.RFactorMax}
							slotProps={{
								inputLabel: { shrink: true },
								htmlInput: { step: 0.001, min: 0.9, max: 2.0 },
							}}
						/>
					</Grid2>
				</Grid2>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button
					variant="contained"
					onClick={onCancel}
					sx={{
						backgroundColor: "#e0e0e0",
						color: "rgba(0, 0, 0, 0.87)",
						"&:hover": { backgroundColor: "#d5d5d5" },
					}}
				>
					CANCEL
				</Button>
				<Button
					color="primary"
					variant="contained"
					onClick={handleSubmit}
					disabled={isSaving}
				>
					{isSaving ? "SAVING..." : "SAVE"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
